import React, { useState, useRef, useEffect } from 'react';
import { Loader2, User, Save, CheckCircle, Camera, Trash2, AlertTriangle } from 'lucide-react';
import { createCandidate, getCandidateProfile, updateCandidate, uploadProfilePhoto, deactivateAccount } from '../services/api';
import BackButton from '../components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ProfilePage.css';

const PROVIDER_LABELS = {
  Google: 'Google hesabı ile giriş yapıldı',
  Facebook: 'Facebook hesabı ile giriş yapıldı',
  LinkedIn: 'LinkedIn hesabı ile giriş yapıldı',
  EmailCode: 'E-posta ile giriş yapıldı',
};

const ProfilePage = () => {
  const { user, updateCandidateId, logout } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(user?.imageUrl || null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const photoRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    email: user?.email || '',
    phone: '',
    title: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    location: '',
    linkedinUrl: '',
    experienceYear: '',
    education: '',
    skills: '',
    summary: '',
    gender: '',
    nationality: '',
    militaryStatus: '',
  });

  // Profil yükle
  useEffect(() => {
    if (!user?.candidateId) return;
    setLoadingProfile(true);
    getCandidateProfile()
      .then((data) => {
        const c = data || {};
        let bDay = '', bMonth = '', bYear = '';
        if (c.birthdate) {
          const d = new Date(c.birthdate);
          if (!isNaN(d)) {
            bDay = String(d.getDate()).padStart(2, '0');
            bMonth = String(d.getMonth() + 1).padStart(2, '0');
            bYear = String(d.getFullYear());
          }
        }
        setForm(prev => ({
          ...prev,
          name: c.name || prev.name,
          surname: c.surname || prev.surname,
          email: c.email || prev.email,
          phone: c.phone || prev.phone,
          title: c.title || prev.title,
          birthDay: bDay || prev.birthDay,
          birthMonth: bMonth || prev.birthMonth,
          birthYear: bYear || prev.birthYear,
          location: c.location || prev.location,
          linkedinUrl: c.linkedinUrl || prev.linkedinUrl,
          experienceYear: c.experienceYear != null ? String(c.experienceYear) : prev.experienceYear,
          education: c.educationLevel || prev.education,
          summary: c.summary || prev.summary,
          gender: c.gender || prev.gender,
          nationality: c.nationality || prev.nationality,
          militaryStatus: c.militaryStatus || prev.militaryStatus,
        }));
        if (c.imageUrl) setProfilePhoto(c.imageUrl);
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, [user?.candidateId]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfilePhoto(URL.createObjectURL(file));
    setPendingPhotoFile(file);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let birthdate = null;
      if (form.birthYear && form.birthMonth && form.birthDay) {
        birthdate = `${form.birthYear}-${form.birthMonth.padStart(2, '0')}-${form.birthDay.padStart(2, '0')}`;
      }

      const payload = {
        name: form.name,
        surname: form.surname,
        email: form.email,
        phone: form.phone || null,
        title: form.title || null,
        birthdate,
        location: form.location || null,
        linkedinUrl: form.linkedinUrl || null,
        summary: form.summary || null,
        militaryStatus: form.militaryStatus || null,
        educationLevel: form.education || null,
        experienceYear: form.experienceYear ? Number(form.experienceYear) : null,
        gender: form.gender || null,
        nationality: form.nationality || null,
      };

      let candidateId = user?.candidateId;

      if (candidateId) {
        // Güncelle
        await updateCandidate(payload);
      } else {
        // İlk kez oluştur
        const result = await createCandidate(payload);
        if (result.status && result.data) {
          candidateId = result.data;
          updateCandidateId(result.data);
        }
      }

      // Fotoğraf yükle
      if (pendingPhotoFile && candidateId) {
        try {
          const photoResult = await uploadProfilePhoto(candidateId, pendingPhotoFile);
          if (photoResult.status && photoResult.data) {
            setProfilePhoto(photoResult.data);
          }
          setPendingPhotoFile(null);
        } catch {
          // Fotoğraf yüklenemezse profil yine de kaydedildi
        }
      }

      setSaved(true);
    } catch (e) {
      // 401 artık oturumu düşürmüyor; API her iki durumda da 401 döndürebildiği
      // için kullanıcıya ne yapabileceğini söylüyoruz
      setError(
        e?.code === 'UNAUTHORIZED'
          ? 'Kaydetme yetkisi doğrulanamadı. Çıkış yapıp tekrar giriş yapmayı deneyin; sorun sürerse bize bildirin.'
          : 'Profil kaydedilemedi. Lütfen tekrar deneyin.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== 'SİL' || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deactivateAccount({ reason: null });
      logout();
      navigate('/', { replace: true });
    } catch (e) {
      setDeleteError(e.message || 'Hesap silinemedi. Lütfen tekrar deneyin.');
      setDeleting(false);
    }
  };

  const displayName = [form.name, form.surname].filter(Boolean).join(' ') || user?.name || 'Kullanıcı';

  return (
    <div className="profile-page">
      <div className="profile__container">
        <BackButton />
        <div className="profile__header">
          <h1 className="profile__title">Profilim</h1>
          <p className="profile__subtitle">
            {user?.candidateId
              ? 'Bilgilerini güncel tut, sana en uygun ilanları bulalım.'
              : 'Profilini oluştur, sana en uygun ilanları bulalım.'}
          </p>
        </div>

        {/* Profil Kartı */}
        <div className="profile__card">
          <div className="profile__card-top">
            <div className="profile__avatar-wrap">
              <div className="profile__avatar">
                {profilePhoto ? (
                  <img src={profilePhoto} alt="" className="profile__avatar-img" />
                ) : (
                  <User size={28} />
                )}
              </div>
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                className="profile__file-input"
                id="profile-photo"
                onChange={handlePhotoChange}
              />
              <label htmlFor="profile-photo" className="profile__avatar-edit">
                <Camera size={12} />
              </label>
            </div>
            <div className="profile__card-info">
              <h2 className="profile__name">{displayName}</h2>
              <p className="profile__provider">{PROVIDER_LABELS[user?.provider] || 'Giriş yapıldı'}</p>
              {user?.email && <p className="profile__email">{user.email}</p>}
            </div>
          </div>
        </div>

        {loadingProfile ? (
          <div className="profile__section" style={{ textAlign: 'center', padding: '40px' }}>
            <Loader2 size={24} className="profile__spinner" style={{ color: '#2F3A8F' }} />
            <p style={{ color: '#6B7280', marginTop: '12px', fontSize: '14px' }}>Profil yükleniyor...</p>
          </div>
        ) : (
          <>
            {/* Kişisel Bilgiler */}
            <div className="profile__section">
              <h2 className="profile__section-title">Kişisel Bilgiler</h2>
              <div className="profile__form-grid">
                <div className="profile__field">
                  <label className="profile__label">Ad</label>
                  <input className="profile__input" value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Ad" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Soyad</label>
                  <input className="profile__input" value={form.surname} onChange={(e) => updateField('surname', e.target.value)} placeholder="Soyad" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">E-posta</label>
                  <input className="profile__input" value={form.email} disabled placeholder="E-posta" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Telefon</label>
                  <input className="profile__input" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+90 5XX XXX XX XX" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Doğum Tarihi</label>
                  <div className="profile__date-group">
                    <input className="profile__date-input" value={form.birthDay} onChange={(e) => updateField('birthDay', e.target.value)} placeholder="GG" maxLength={2} />
                    <span className="profile__date-sep">/</span>
                    <input className="profile__date-input" value={form.birthMonth} onChange={(e) => updateField('birthMonth', e.target.value)} placeholder="AA" maxLength={2} />
                    <span className="profile__date-sep">/</span>
                    <input className="profile__date-input profile__date-input--year" value={form.birthYear} onChange={(e) => updateField('birthYear', e.target.value)} placeholder="YYYY" maxLength={4} />
                  </div>
                </div>
                <div className="profile__field">
                  <label className="profile__label">Cinsiyet</label>
                  <div className="profile__radio-group">
                    {['Erkek', 'Kadın', 'Belirtmek istemiyorum'].map(opt => (
                      <label key={opt} className={`profile__radio ${form.gender === opt ? 'profile__radio--active' : ''}`}>
                        <input type="radio" name="gender" value={opt} checked={form.gender === opt} onChange={(e) => updateField('gender', e.target.value)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Kariyer Bilgileri */}
            <div className="profile__section">
              <h2 className="profile__section-title">Kariyer Bilgileri</h2>
              <p className="profile__section-hint">Bu bilgiler sana uygun ilanları bulmamızda kullanılır.</p>
              <div className="profile__form-grid">
                <div className="profile__field">
                  <label className="profile__label">Aradığın Pozisyon</label>
                  <input className="profile__input" value={form.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Garson, Yazılımcı, Muhasebeci..." />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Şehir / İl</label>
                  <input className="profile__input" value={form.location} onChange={(e) => updateField('location', e.target.value)} placeholder="İstanbul, Ankara..." />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Deneyim (Yıl)</label>
                  <input className="profile__input" type="number" min="0" value={form.experienceYear} onChange={(e) => updateField('experienceYear', e.target.value)} placeholder="0" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Eğitim Durumu</label>
                  <div className="profile__select-wrap">
                    <select className="profile__select" value={form.education} onChange={(e) => updateField('education', e.target.value)}>
                      <option value="">Seçiniz</option>
                      <option value="İlkokul">İlkokul</option>
                      <option value="Ortaokul">Ortaokul</option>
                      <option value="Lise">Lise</option>
                      <option value="Ön Lisans">Ön Lisans</option>
                      <option value="Lisans">Lisans</option>
                      <option value="Yüksek Lisans">Yüksek Lisans</option>
                      <option value="Doktora">Doktora</option>
                    </select>
                  </div>
                </div>
                <div className="profile__field">
                  <label className="profile__label">Askerlik Durumu</label>
                  <div className="profile__select-wrap">
                    <select className="profile__select" value={form.militaryStatus} onChange={(e) => updateField('militaryStatus', e.target.value)}>
                      <option value="">Seçiniz</option>
                      <option value="Tamamlandı">Tamamlandı</option>
                      <option value="Muaf">Muaf</option>
                      <option value="Tecilli">Tecilli</option>
                      <option value="Yapılmadı">Yapılmadı</option>
                    </select>
                  </div>
                </div>
                <div className="profile__field">
                  <label className="profile__label">LinkedIn</label>
                  <input className="profile__input" value={form.linkedinUrl} onChange={(e) => updateField('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>
              <div className="profile__field profile__field--full">
                <label className="profile__label">Yetkinlikler</label>
                <input className="profile__input" value={form.skills} onChange={(e) => updateField('skills', e.target.value)} placeholder="React, Servis, Muhasebe... (virgülle ayır)" />
              </div>
              <div className="profile__field profile__field--full">
                <label className="profile__label">Hakkında</label>
                <textarea className="profile__textarea" rows={3} value={form.summary} onChange={(e) => updateField('summary', e.target.value)} placeholder="Kısa bir özet yaz..." />
              </div>
            </div>

            {/* Form aksiyonları */}
            <div className="profile__actions">
              <div className="profile__actions-feedback" aria-live="polite">
                {error && <p className="profile__error">{error}</p>}
                {saved && !saving && (
                  <p className="profile__success"><CheckCircle size={16} /> Değişikliklerin kaydedildi.</p>
                )}
              </div>
              <button className="profile__save" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <><Loader2 size={16} className="profile__spinner" /> Kaydediliyor...</>
                ) : (
                  <><Save size={16} /> Profili Kaydet</>
                )}
              </button>
            </div>

            {/* Hesap silme */}
            <section className="profile__danger">
              <div className="profile__danger-icon" aria-hidden="true">
                <AlertTriangle size={20} />
              </div>
              <div className="profile__danger-content">
                <h2 className="profile__danger-title">Hesabımı sil</h2>
                <p className="profile__danger-desc">
                  Hesabınız kapatılır; profiliniz, CV'leriniz ve başvurularınız artık
                  görüntülenemez. Varsa aktif aboneliğiniz iptal edilir. Bu işlem geri alınamaz.
                </p>
              </div>
              <button className="profile__danger-btn" onClick={() => setShowDelete(true)}>
                <Trash2 size={15} /> Hesabımı Sil
              </button>
            </section>
          </>
        )}

        {showDelete && (
          <div
            className="profile__overlay"
            onClick={(e) => e.target === e.currentTarget && !deleting && setShowDelete(false)}
          >
            <div className="profile__confirm" role="dialog" aria-modal="true">
              <h3 className="profile__confirm-title">Hesabını silmek istediğine emin misin?</h3>
              <p className="profile__confirm-text">
                Bu işlem geri alınamaz. Devam etmek için aşağıya <strong>SİL</strong> yaz.
              </p>
              <input
                className="profile__confirm-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="SİL"
                autoFocus
              />

              {deleteError && (
                <p className="profile__confirm-error">
                  {deleteError}
                </p>
              )}

              <div className="profile__confirm-actions">
                <button
                  className="profile__danger-btn profile__danger-btn--solid"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm.trim().toUpperCase() !== 'SİL' || deleting}
                >
                  {deleting ? (
                    <><Loader2 size={15} className="profile__spinner" /> Siliniyor...</>
                  ) : (
                    <><Trash2 size={15} /> Hesabımı Sil</>
                  )}
                </button>
                <button
                  className="profile__confirm-cancel"
                  onClick={() => setShowDelete(false)}
                  disabled={deleting}
                >
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
